import Slider from "@react-native-community/slider";
import { type ReactNode } from "react";
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { colors, fonts, radius, space, tabular, webNoOutline } from "../theme";

/** Labelled field wrapper with optional help text and inline error. */
export function Field({
  label,
  help,
  error,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      {help !== undefined ? <Text style={styles.help}>{help}</Text> : null}
      {children}
      {error !== undefined ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  );
}

export interface OptionItem<T extends string> {
  value: T;
  label: string;
  sub?: string;
}

/** Radio-style option cards (one-question-per-screen flow): a ring dot, a
 * title and an optional sub-line. Min height 64px for comfortable tap targets
 * (handoff: option cards min-height 64px). */
export function OptionList<T extends string>({
  options,
  value,
  onChange,
  testIDPrefix = "opt",
}: {
  options: ReadonlyArray<OptionItem<T>>;
  value: T | null;
  onChange: (value: T) => void;
  testIDPrefix?: string;
}): JSX.Element {
  return (
    <View style={styles.optList}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <Pressable
            key={opt.value}
            onPress={() => onChange(opt.value)}
            testID={`${testIDPrefix}-${opt.value}`}
            accessibilityRole="radio"
            accessibilityState={{ selected }}
            style={[styles.opt, selected && styles.optSelected]}
          >
            <View style={[styles.optDot, selected && styles.optDotSelected]}>
              {selected ? <View style={styles.optDotInner} /> : null}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.optLabel}>{opt.label}</Text>
              {opt.sub !== undefined ? (
                <Text style={styles.optSub}>{opt.sub}</Text>
              ) : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

/** Compact single-select segmented control (inline toggles, year-known, etc.). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  testIDPrefix = "seg",
}: {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  testIDPrefix?: string;
}): JSX.Element {
  return (
    <View style={styles.segmented}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <Pressable
            key={opt.value}
            onPress={() => onChange(opt.value)}
            testID={`${testIDPrefix}-${opt.value}`}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            style={[styles.segment, selected && styles.segmentSelected]}
          >
            <Text
              style={[styles.segmentText, selected && styles.segmentTextSelected]}
            >
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/** A button grid (e.g. state/territory). Wraps; each cell is selectable. */
export function ButtonGrid<T extends string>({
  options,
  value,
  onChange,
  testIDPrefix = "grid",
}: {
  options: ReadonlyArray<T>;
  value: T | null;
  onChange: (value: T) => void;
  testIDPrefix?: string;
}): JSX.Element {
  return (
    <View style={styles.grid}>
      {options.map((opt) => {
        const selected = opt === value;
        return (
          <Pressable
            key={opt}
            onPress={() => onChange(opt)}
            testID={`${testIDPrefix}-${opt}`}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            style={[styles.gridCell, selected && styles.gridCellSelected]}
          >
            <Text
              style={[styles.gridText, selected && styles.gridTextSelected]}
            >
              {opt}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/** Integer stepper with min/max clamp (children count). */
export function Stepper({
  value,
  onChange,
  min,
  max,
  suffix,
  testID,
  accessibilityLabel,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  suffix?: string;
  testID?: string;
  /** Field name announced by screen readers — without it the +/- buttons read
   * as bare "Increase"/"Decrease" with no idea what they change. */
  accessibilityLabel?: string;
}): JSX.Element {
  const set = (next: number): void => {
    onChange(Math.max(min, Math.min(max, next)));
  };
  const valueLabel =
    accessibilityLabel !== undefined
      ? `${accessibilityLabel}: ${value}${suffix !== undefined ? ` ${suffix}` : ""}`
      : undefined;
  return (
    <View style={styles.stepper}>
      <StepperButton
        label="−"
        disabled={value <= min}
        onPress={() => set(value - 1)}
        testID={testID ? `${testID}-dec` : undefined}
        context={accessibilityLabel}
      />
      <Text
        style={styles.stepperValue}
        testID={testID ? `${testID}-value` : undefined}
        accessibilityLabel={valueLabel}
      >
        {value}
        {suffix !== undefined ? ` ${suffix}` : ""}
      </Text>
      <StepperButton
        label="+"
        disabled={value >= max}
        onPress={() => set(value + 1)}
        testID={testID ? `${testID}-inc` : undefined}
        context={accessibilityLabel}
      />
    </View>
  );
}

function StepperButton({
  label,
  onPress,
  disabled,
  testID,
  context,
}: {
  label: string;
  onPress: () => void;
  disabled: boolean;
  testID?: string;
  context?: string;
}): JSX.Element {
  const action = label === "+" ? "Increase" : "Decrease";
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={context !== undefined ? `${action} ${context}` : action}
      style={[styles.stepperBtn, disabled && styles.stepperBtnDisabled]}
    >
      <Text style={styles.stepperBtnText}>{label}</Text>
    </Pressable>
  );
}

/** Continuous slider with a giant honey value read-out and end labels. Pass
 * `hideValue` when the caller renders its own read-out (e.g. the what-if cards),
 * so the value isn't printed twice. */
export function SliderInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  maxLabel,
  hideValue = false,
  accessibilityLabel,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  maxLabel?: string;
  hideValue?: boolean;
  accessibilityLabel?: string;
}): JSX.Element {
  return (
    <View style={styles.sliderWrap}>
      {hideValue ? null : (
        <Text style={styles.sliderValue}>
          {value}
          {unit !== undefined ? <Text style={styles.sliderUnit}> {unit}</Text> : null}
        </Text>
      )}
      <Slider
        style={styles.slider}
        minimumValue={min}
        maximumValue={max}
        step={step}
        value={value}
        onValueChange={(v) => onChange(Math.round(v))}
        minimumTrackTintColor={colors.honey}
        maximumTrackTintColor={colors.line}
        thumbTintColor={colors.honey}
        accessibilityRole="adjustable"
        accessibilityLabel={accessibilityLabel}
        accessibilityValue={{ min, max, now: value }}
      />
      <View style={styles.sliderBounds}>
        <Text style={styles.sliderBoundText}>{min}</Text>
        <Text style={styles.sliderBoundText}>{maxLabel ?? String(max)}</Text>
      </View>
    </View>
  );
}

/** Numeric money input, `$`-prefixed and comma-formatted live (en-AU). Holds a
 * digits-only string; the parent parses on submit. */
export function MoneyInput({
  value,
  onChange,
  placeholder,
  testID,
  accessibilityLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  testID?: string;
  /** Spoken name for the field — the visible `Field` label isn't
   * programmatically tied to the input, so pass it here for screen readers. */
  accessibilityLabel?: string;
}): JSX.Element {
  const display =
    value === "" ? "" : Number(value).toLocaleString("en-AU");
  return (
    <View style={styles.moneyWrap}>
      <Text style={styles.moneyPrefix}>$</Text>
      <TextInput
        value={display}
        onChangeText={(t) => onChange(t.replace(/[^0-9]/g, ""))}
        keyboardType="number-pad"
        placeholder={placeholder ?? "0"}
        placeholderTextColor={colors.muted}
        testID={testID}
        accessibilityLabel={
          accessibilityLabel === undefined
            ? undefined
            : `${accessibilityLabel}, in dollars`
        }
        style={styles.moneyInput}
      />
    </View>
  );
}

/** Opt-in checkbox with an inline label (ACMA email consent — unchecked by
 * default; no pre-ticked consent, validation §3 / Spam Act). */
export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <Pressable
      onPress={() => onChange(!checked)}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      style={styles.checkboxRow}
    >
      <View style={[styles.checkboxBox, checked && styles.checkboxBoxChecked]}>
        {checked ? <Text style={styles.checkboxTick}>✓</Text> : null}
      </View>
      <Text style={styles.checkboxLabel}>{label}</Text>
    </Pressable>
  );
}

/** Yes/No rendered as two option cards. */
export function YesNo({
  value,
  onChange,
  yesLabel = "Yes",
  noLabel = "No",
  yesSub,
  noSub,
  testIDPrefix = "yesno",
}: {
  value: boolean | null;
  onChange: (value: boolean) => void;
  yesLabel?: string;
  noLabel?: string;
  yesSub?: string;
  noSub?: string;
  testIDPrefix?: string;
}): JSX.Element {
  return (
    <OptionList<"yes" | "no">
      options={[
        { value: "yes", label: yesLabel, sub: yesSub },
        { value: "no", label: noLabel, sub: noSub },
      ]}
      value={value === null ? null : value ? "yes" : "no"}
      onChange={(v) => onChange(v === "yes")}
      testIDPrefix={testIDPrefix}
    />
  );
}

const styles = StyleSheet.create({
  field: { gap: space.sm },
  label: { fontFamily: fonts.bodySemibold, fontSize: 15, color: colors.ink },
  help: {
    fontFamily: fonts.body,
    fontSize: 13,
    color: colors.inkSoft,
    lineHeight: 19,
  },
  error: { fontFamily: fonts.bodyMedium, fontSize: 13, color: colors.danger },

  // option cards
  optList: { gap: space.sm },
  opt: {
    flexDirection: "row",
    alignItems: "center",
    gap: space.md,
    minHeight: 64,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    borderRadius: radius.md,
    borderWidth: 1.5,
    borderColor: colors.line,
    backgroundColor: colors.card,
  },
  optSelected: { borderColor: colors.honey, backgroundColor: colors.honeyTint },
  optDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.lineStrong,
    alignItems: "center",
    justifyContent: "center",
  },
  optDotSelected: { borderColor: colors.honeyDeep },
  optDotInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.honeyDeep,
  },
  optLabel: { fontFamily: fonts.bodySemibold, fontSize: 16, color: colors.ink },
  optSub: {
    fontFamily: fonts.body,
    fontSize: 13.5,
    color: colors.inkSoft,
    marginTop: 2,
  },

  // segmented
  segmented: { flexDirection: "row", gap: space.sm, flexWrap: "wrap" },
  segment: {
    paddingVertical: space.sm + 2,
    paddingHorizontal: space.lg,
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.line,
    backgroundColor: colors.card,
  },
  segmentSelected: { borderColor: colors.honey, backgroundColor: colors.honeyTint },
  segmentText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.ink },
  segmentTextSelected: { color: colors.honeyDeep },

  // button grid
  grid: { flexDirection: "row", flexWrap: "wrap", gap: space.sm },
  gridCell: {
    minWidth: 64,
    flexGrow: 1,
    paddingVertical: space.md,
    alignItems: "center",
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.line,
    backgroundColor: colors.card,
  },
  gridCellSelected: { borderColor: colors.honey, backgroundColor: colors.honeyTint },
  gridText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.ink },
  gridTextSelected: { color: colors.honeyDeep },

  // stepper
  stepper: { flexDirection: "row", alignItems: "center", gap: space.lg },
  stepperValue: {
    fontFamily: fonts.displayHeavy,
    fontSize: 20,
    color: colors.ink,
    minWidth: 72,
    textAlign: "center",
  },
  stepperBtn: {
    width: 48,
    height: 48,
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.line,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.card,
  },
  stepperBtnDisabled: { opacity: 0.4 },
  stepperBtnText: { fontSize: 22, color: colors.honeyDeep, fontFamily: fonts.bodyBold },

  // slider
  sliderWrap: { gap: space.xs },
  sliderValue: {
    fontFamily: fonts.displayHeavy,
    fontSize: 52,
    color: colors.honeyDeep,
    letterSpacing: -1.5,
    ...tabular,
  },
  sliderUnit: { fontFamily: fonts.body, fontSize: 18, color: colors.muted },
  slider: { width: "100%", height: 40 },
  sliderBounds: { flexDirection: "row", justifyContent: "space-between" },
  sliderBoundText: { fontFamily: fonts.mono, fontSize: 12, color: colors.muted },

  // money
  moneyWrap: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingHorizontal: space.lg,
    backgroundColor: colors.card,
  },
  moneyPrefix: {
    fontFamily: fonts.bodySemibold,
    fontSize: 18,
    color: colors.muted,
    marginRight: space.xs,
  },
  moneyInput: {
    flex: 1,
    paddingVertical: 15,
    fontFamily: fonts.bodyMedium,
    fontSize: 18,
    color: colors.ink,
    ...tabular,
    ...webNoOutline,
  },

  // checkbox
  checkboxRow: { flexDirection: "row", gap: space.sm, alignItems: "flex-start" },
  checkboxBox: {
    width: 22,
    height: 22,
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  checkboxBoxChecked: { borderColor: colors.honey, backgroundColor: colors.honey },
  checkboxTick: { color: colors.ink, fontSize: 14, fontWeight: "800", lineHeight: 18 },
  checkboxLabel: {
    flex: 1,
    fontFamily: fonts.body,
    fontSize: 13,
    lineHeight: 19,
    color: colors.inkSoft,
  },
});
